/**
 * InvestmentCostCalculator - "What Am I Giving Up?" opportunity cost calculator
 * Shows users what their purchase could be worth if invested instead.
 */
import { useState, useEffect } from "react";
import { ChevronRight, Lock, X, Info } from "lucide-react";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { formatCurrency, getCurrentCurrency } from "../../utils/formatters";

export const RATE_PRESETS = [
  { label: 'VFV', rate: 10.5 },
  { label: 'VEQT', rate: 7.4 },
  { label: 'VBAL', rate: 7.1 },
  { label: 'Custom', rate: null },
];

export const PERIODS = [5, 10, 15, 20];

export const CARD_COLORS = [
  { bg: 'bg-green-50', border: 'border-green-200', yearText: 'text-green-600' },
  { bg: 'bg-blue-50', border: 'border-blue-200', yearText: 'text-blue-600' },
  { bg: 'bg-purple-50', border: 'border-purple-200', yearText: 'text-purple-600' },
  { bg: 'bg-pink-50', border: 'border-pink-200', yearText: 'text-pink-600' },
];

const InvestmentCostCalculator = ({ variant = 'card' }) => {
  const { hasAccess, showFeaturePaywall, theme } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(0); // VEQT default
  const [customRate, setCustomRate] = useState('');
  const [showRateTooltip, setShowRateTooltip] = useState(false);
  
  const isPremium = hasAccess('opportunityCost');
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const activeRate = RATE_PRESETS[selectedPreset].rate !== null 
    ? RATE_PRESETS[selectedPreset].rate 
    : (parseFloat(customRate) || 0);
  const purchaseAmount = parseFloat(amount) || 0;

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const calculate = (years) => {
    if (!purchaseAmount || !activeRate) return { futureValue: 0, gain: 0, gainPercent: 0 };
    const r = activeRate / 100;
    const futureValue = purchaseAmount * Math.pow(1 + r, years);
    const gain = futureValue - purchaseAmount;
    const gainPercent = (gain / purchaseAmount) * 100;
    return { futureValue, gain, gainPercent };
  };

  const getTriggerStyle = () => ({
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  });

  const getHeaderGradient = () => {
    if (isWarm) return 'linear-gradient(135deg, #EDE3D8, #F0E5D2)';
    if (isPink) return '#FFFFFF';
    return 'linear-gradient(135deg, #E0E7FF, #DBEAFE)';
  };

  const getPresetGradient = () => {
    if (isWarm) return 'linear-gradient(135deg, #2D5A3D, #1B4027)';
    if (isPink) return 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)';
    return 'linear-gradient(135deg, #4361EE, #6366f1)';
  };

  const getThemeColor = () => {
    if (isWarm) return '#8B7355';
    if (isPink) return '#E27BA1';
    return '#4338ca';
  };

  const getBadgeStyle = () => {
    if (isWarm) return 'bg-[#8B7355] text-white';
    if (isPink) return 'bg-pink-500 text-white';
    return 'bg-indigo-600 text-white';
  };

  const currencySymbol = getCurrentCurrency().symbol;
  const result20yr = calculate(20);

  const handleTriggerClick = () => {
    if (isPremium) setIsOpen(true);
    else showFeaturePaywall('opportunityCost');
  };

  return (
    <>
      {variant === 'row' ? (
        // Home lists the buying tools as rows inside one shared card.
        <button
          onClick={handleTriggerClick}
          className="w-full bg-white px-4 py-3.5 text-left"
          data-testid="investment-cost-trigger"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
              style={{ background: theme === 'warm' ? '#F0E6D8' : theme === 'blue' ? '#EEF1FE' : '#F3ECF7' }}
            >
              {isPremium ? '📈' : <Lock className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="m-0 text-[15px] truncate" style={{ color: '#1F2937', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                What Am I Giving Up?
              </p>
              <p className="m-0 mt-0.5 text-[12px] text-gray-500 truncate">
                {isPremium ? 'See your purchase as future investment' : 'Upgrade to unlock'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
          </div>
        </button>
      ) : variant === 'tile' ? (
        // Wishlist shows the buying tools as two squares at the top.
        <button
          onClick={handleTriggerClick}
          className="w-full h-full rounded-2xl p-3.5 text-left flex flex-col"
          style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', minHeight: '118px' }}
          data-testid="investment-cost-tile"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ background: (theme === 'warm' ? '#F0E6D8' : theme === 'blue' ? '#EEF1FE' : '#F3ECF7') }}>
            📈
          </div>
          <p className="m-0 mt-3 text-[15px]" style={{ color: '#1F2937', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>What Am I<br />Giving Up?</p>
          <p className="m-0 mt-1 text-[12px] text-gray-500 leading-snug">See it invested instead</p>
        </button>
      ) : (
      <button
        onClick={handleTriggerClick}
        className="w-full rounded-2xl p-3 transition-all text-left group relative overflow-hidden"
        style={getTriggerStyle()}
        data-testid="investment-cost-trigger"
      >

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {isPremium ? <span className="text-2xl">📈</span> : <Lock className="w-5 h-5 text-gray-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg text-gray-900" style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.3px' }}>What Am I Giving Up?</h3>
            <p className="text-xs text-gray-500">{isPremium ? 'See your purchase as future investment' : 'Upgrade to unlock'}</p>
          </div>
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isPremium ? (isWarm ? 'rgba(124,92,58,0.10)' : isPink ? 'rgba(236,72,153,0.10)' : 'rgba(99,102,241,0.10)') : 'rgba(156,163,175,0.10)' }}>
            <ChevronRight className="w-4 h-4" style={{ color: isPremium ? (isWarm ? '#7C5C3A' : isPink ? '#EC4899' : '#6366f1') : '#9CA3AF' }} />
          </div>
        </div>
      </button>
      )}

      {/* Full-screen Calculator Sheet */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9999] flex flex-col wy-plan-tool" data-patch="plan_10" 
          style={{ background: isWarm ? '#F8F3EB' : isPink ? '#FBEFF5' : '#FFFFFF', paddingTop: 'max(env(safe-area-inset-top), 12px)' }}
        >
          {/* Gradient Header */}
          <div className="relative mx-4 mt-2 px-5 pt-4 pb-5 rounded-[28px] overflow-hidden" style={{ background: getHeaderGradient() }}>
            
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">📈</span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-gray-800 text-xl font-bold" style={{ fontFamily: "'DM Serif Display', serif" }}>What am I giving up?</h2>
                  <p className="text-gray-500 text-xs">Considering a purchase? How much would you have if you invested the money into an index fund instead?</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"
                data-testid="investment-calc-close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overscroll-none px-5 py-5 pb-24" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Amount Input + Rate Display */}
            <div className="flex gap-3 mb-6">
              <div className="flex-1 bg-white rounded-2xl px-4 py-3 shadow-sm">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">Purchase Amount</label>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg text-gray-300 font-medium">{currencySymbol}</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    className="text-2xl font-bold text-gray-800 bg-transparent outline-none w-full"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                    data-testid="investment-calc-amount"
                  />
                </div>
              </div>
              <div className="w-24 bg-white rounded-2xl px-3 py-3 shadow-sm text-center flex flex-col items-center justify-center">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">Rate</label>
                <p className="text-2xl font-bold leading-none" style={{ color: getThemeColor(), fontFamily: "'DM Serif Display', serif" }}>
                  {activeRate}%
                </p>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Per Year</p>
              </div>
            </div>

            {/* Rate Presets */}
            <div className="mb-6 relative">
              <div className="flex items-center gap-1.5 mb-3">
                <label className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: '#7B5EA7' }}><span className="wy-star">✦ </span>Rate of return</label>
                <button 
                  onClick={() => setShowRateTooltip(!showRateTooltip)}
                  className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center"
                  data-testid="rate-info-tooltip"
                >
                  <Info className="w-2.5 h-2.5 text-gray-500" />
                </button>
              </div>
              {showRateTooltip && (
                <div className="absolute top-6 left-0 right-0 z-10 bg-gray-100 text-gray-700 text-xs rounded-xl p-3 shadow-lg border border-gray-200" onClick={() => setShowRateTooltip(false)}>
                  <p className="leading-relaxed">Rates based on historical average annual returns. Past performance does not guarantee future results.</p>
                </div>
              )}
              <div className="flex gap-2">
                {RATE_PRESETS.map((preset, i) => (
                  <button
                    key={preset.label}
                    onClick={() => setSelectedPreset(i)}
                    className={`flex-1 py-2.5 rounded-2xl text-center transition-all shadow-sm ${
                      selectedPreset === i
                        ? 'text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-100'
                    }`}
                    style={selectedPreset === i ? { background: getPresetGradient() } : undefined}
                    data-testid={`rate-preset-${preset.label}`}
                  >
                    <span className="text-sm font-bold block">{preset.label}</span>
                    <span className={`text-xs ${selectedPreset === i ? 'text-white/80' : 'text-gray-400'}`}>
                      {preset.rate ? `${preset.rate}%` : '\u2014'}
                    </span>
                  </button>
                ))}
              </div>
              {RATE_PRESETS[selectedPreset].rate === null && (
                <div className="mt-3 relative">
                  <input
                    type="number"
                    value={customRate}
                    onChange={(e) => setCustomRate(e.target.value)}
                    placeholder="Enter rate"
                    className="w-full px-4 py-3 rounded-2xl bg-white border border-gray-100 text-sm font-bold text-gray-800 focus:outline-none shadow-sm"
                    data-testid="custom-rate-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">%</span>
                </div>
              )}
            </div>

            {/* Results */}
            {purchaseAmount > 0 && activeRate > 0 && (
              <>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 block">
                  Your {currencySymbol}{purchaseAmount.toLocaleString()} could become
                </label>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  {PERIODS.map((years, idx) => {
                    const { futureValue, gain, gainPercent } = calculate(years);
                    const colors = CARD_COLORS[idx];
                    const isHighlight = years === 20;
                    return (
                      <div
                        key={years}
                        className={`rounded-2xl p-4 border relative ${colors.bg} ${colors.border}`}
                        data-testid={`result-${years}yr`}
                      >
                        {isHighlight && (
                          <span className={`absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${getBadgeStyle()}`}>
                            Best
                          </span>
                        )}
                        <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${colors.yearText}`}>
                          {years} Years
                        </p>
                        <p 
                          className={`text-2xl font-bold mb-1.5 ${isHighlight ? colors.yearText : 'text-gray-800'}`}
                          style={{ fontFamily: "'DM Serif Display', serif" }}
                        >
                          {formatCurrency(futureValue)}
                        </p>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-bold text-emerald-500">&#8593; +{formatCurrency(gain)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{gainPercent.toFixed(0)}% growth</p>
                      </div>
                    );
                  })}
                </div>

                {/* Bottom Insight */}
                <div className="rounded-2xl p-4 bg-gray-100/80 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-lg">💡</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    In <span className="font-bold">20 years</span> that {formatCurrency(purchaseAmount)} could be worth{' '}
                    <span className="font-bold" style={{ color: getThemeColor() }}>{formatCurrency(result20yr.futureValue)}</span>
                    {' '}&mdash; that's <span className="font-bold">{(result20yr.futureValue / purchaseAmount).toFixed(1)}x</span> what you'd spend today.
                  </p>
                </div>
              </>
            )}

            {/* Empty state */}
            {(!purchaseAmount || !activeRate) && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">📈</span>
                </div>
                <p className="text-sm text-gray-400">
                  {purchaseAmount > 0 && !activeRate
                    ? 'Enter a growth rate to see what you\'re giving up'
                    : 'Enter an amount to see what you\'re giving up'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InvestmentCostCalculator;
